import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { CinematicBackdrop } from '@/components/brand/CinematicBackdrop';
import { PageTransition } from '@/components/layout/Motion';
import { env } from '@/config/env';
import { SpartanLogo } from '@/components/brand/SpartanLogo';
import { Button, LinkButton } from '@/components/ui/Button';
import { RankShield } from '@/components/brand/RankShield';
import { SUPPORTED_LANGUAGES } from '@/i18n';
import { cn } from '@/lib/cn';
import { useLanguage, useOnline } from '@/lib/hooks';
import { useSeoTags } from '@/lib/seo';
import { routes, translatePath } from '@/routes/paths';
import { useAuth } from '@/state/AuthContext';
import { useTheme } from '@/state/ThemeContext';
import type { Language } from '@/types/domain';

import {
  IconAgora,
  IconForum,
  IconGlobe,
  IconMoon,
  IconRanking,
  IconShieldAdmin,
  IconSun,
  IconTrophy,
  IconUser,
} from './Icons';
import { OfflineBanner, PwaPrompts } from './PwaPrompts';

/**
 * Armazon de la aplicacion (RNF-01).
 * Navegacion inferior en movil y lateral en escritorio, con los mismos
 * destinos, para que cambiar de dispositivo no obligue a reaprender nada.
 */

interface NavItem {
  key: string;
  to: (l: Language) => string;
  label: string;
  Icon: (props: { className?: string }) => JSX.Element;
  requiresAuth?: boolean;
  requiresModerator?: boolean;
  /**
   * Sale de esta aplicacion. El panel vive en otra direccion y otro paquete,
   * asi que no lo puede resolver el router: hace falta una carga completa.
   */
  external?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { key: 'home', to: routes.home, label: 'nav.home', Icon: IconAgora },
  { key: 'competitions', to: routes.competitions, label: 'nav.competitions', Icon: IconTrophy },
  // El foro va antes del ranking: se entra mas a hablar que a mirar puestos.
  { key: 'forum', to: routes.forum, label: 'nav.forum', Icon: IconForum },
  { key: 'rankings', to: routes.rankings, label: 'nav.rankings', Icon: IconRanking },
  { key: 'profile', to: routes.profile, label: 'nav.profile', Icon: IconUser, requiresAuth: true },
  {
    key: 'admin',
    // El panel ya no es una ruta de esta aplicacion: tiene su propia direccion
    // (ver src/AdminApp.tsx). Dejar aqui `routes.admin(l)` mandaba a los
    // moderadores a una pagina que no existe.
    to: () => env.adminUrl,
    label: 'nav.admin',
    Icon: IconShieldAdmin,
    requiresModerator: true,
    external: true,
  },
];

export function AppShell() {
  const { t } = useTranslation();
  const { isAuthenticated, isModerator, loading } = useAuth();
  const online = useOnline();
  const language = useLanguage();
  const location = useLocation();

  // Canonical, og:url y hreflang de CADA pantalla (src/lib/seo.ts): el HTML
  // solo puede traerlos fijos, y apuntando a la portada.
  useSeoTags();

  // Dos mundos visuales en la misma web. La portada publica —la raiz del
  // idioma sin sesion— conserva el aspecto espartano; todo lo demas es el
  // parque. El script de index.html lo fija antes del primer pintado y aqui
  // se corrige en cuanto se sabe si hay sesion.
  const onLanding =
    !isAuthenticated &&
    location.pathname.replace(/\/+$/, '') === routes.home(language).replace(/\/+$/, '');

  useEffect(() => {
    if (loading) return;
    const root = document.documentElement;
    if (onLanding) root.setAttribute('data-world', 'espartano');
    else root.removeAttribute('data-world');
  }, [onLanding, loading]);

  // Al salir del armazon (acceso, onboarding) se vuelve al parque.
  useEffect(() => () => document.documentElement.removeAttribute('data-world'), []);

  // En el movil caben cinco destinos. Con el foro dentro, el escudo del panel
  // se queda fuera de la barra de abajo: para eso esta el boton de
  // "Administracion" del perfil, que ademas se encuentra antes.
  const items = NAV_ITEMS.filter((item) => {
    if (item.requiresModerator) return isModerator;
    if (item.requiresAuth) return isAuthenticated;
    return true;
  });

  return (
    <div className="flex min-h-dvh flex-col">
      {/*
        Decorado de fondo: va detrás de todo (-z-10) y no intercepta clics.
        Es escenografía espartana (hoplita, brasas, barrido dorado), así que
        solo se monta en la portada. El parque es suelo liso: las placas
        tienen que leerse sin nada moviéndose detrás.
      */}
      {onLanding && <CinematicBackdrop />}

      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[70] focus:rounded focus:bg-ink focus:px-4 focus:py-2 focus:text-bg"
      >
        {t('nav.skipToContent')}
      </a>

      <TopBar items={items} />

      <div className="mx-auto flex w-full max-w-7xl flex-1 gap-6 px-4 md:px-6">
        <SideNav items={items} />

        {/* Al menos una pantalla de alto: mientras llega la pagina, el pie se
            queda bajo el pliegue y no salta hacia abajo al cargar (CLS). */}
        <main
          id="main"
          className="min-h-[calc(100dvh-4rem)] min-w-0 flex-1 py-5 pb-24 md:py-8 md:pb-10"
        >
          {!online && <OfflineBanner className="mb-4" />}
          {/* Cada navegación entra con un corte, no aparece de golpe. */}
          <PageTransition>
            <Outlet />
          </PageTransition>
        </main>
      </div>

      <SiteFooter />
      <BottomNav items={items} />
      <PwaPrompts />
    </div>
  );
}

// ---------------------------------------------------------------------------

function TopBar({ items }: { items: NavItem[] }) {
  const { t } = useTranslation();
  const language = useLanguage();
  const { isAuthenticated, profile } = useAuth();

  return (
    <header className="shell-bar-top sticky top-0 z-40">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center gap-3 px-4 md:px-6">
        <NavLink to={routes.home(language)} className="group flex shrink-0 items-center gap-2.5">
          <SpartanLogo
            className="h-9 w-9 transition-transform duration-200 group-hover:scale-105"
            title={t('common.appName')}
          />
          {/* El nombre ya lo da el `alt` del escudo: el texto es solo visual. */}
          <span className="hidden font-display text-lg uppercase sm:inline" aria-hidden="true">
            CaliPeak
          </span>
        </NavLink>

        {/* En escritorio la navegacion principal vive en el lateral; aqui solo
            quedan los controles globales. */}
        {/* Solo entre lg y xl: por encima ya se ve la navegacion lateral. */}
        <nav className="ml-auto hidden items-center gap-1 lg:flex xl:hidden" aria-label={t('nav.menu')}>
          {items
            .filter((item) => item.key !== 'profile' && item.key !== 'admin')
            .map((item) => (
              <NavLink
                key={item.key}
                to={item.to(language)}
                end={item.key === 'home'}
                className={({ isActive }) =>
                  cn(
                    'rounded-[var(--btn-radius)] px-3 py-1.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-surface-2 text-ink ring-1 ring-inset ring-line'
                      : 'text-ink-muted hover:bg-surface-2/60 hover:text-ink',
                  )
                }
              >
                {t(item.label)}
              </NavLink>
            ))}
        </nav>

        <div className={cn('flex items-center gap-1', 'lg:ml-0 ml-auto')}>
          <LanguageSwitcher />
          <ThemeToggle />

          {isAuthenticated && profile ? (
            <NavLink
              to={routes.profile(language)}
              className="ml-1 flex items-center gap-2 rounded-[var(--btn-radius)] px-2 py-1.5 transition-colors hover:bg-surface-2"
            >
              <RankShield level={profile.rank_level} size="xs" />
              {/* Alias tambien en movil: solo el escudo con su cifra parecia un contador de avisos. */}
              <span className="max-w-[14ch] truncate font-display text-sm font-semibold uppercase sm:max-w-[16ch]">
                {profile.alias}
              </span>
            </NavLink>
          ) : (
            <LinkButton to={routes.login(language)} size="sm" className="ml-1">
              {t('nav.login')}
            </LinkButton>
          )}
        </div>
      </div>
      <div className="tube-rule" aria-hidden="true" />
    </header>
  );
}

function SideNav({ items }: { items: NavItem[] }) {
  const { t } = useTranslation();
  const language = useLanguage();

  return (
    <nav
      className="sticky top-20 hidden h-fit w-56 shrink-0 flex-col gap-1 py-8 md:flex lg:hidden xl:flex"
      aria-label={t('nav.menu')}
    >
      {items.map(({ key, to, label, Icon, external }) =>
        // El panel esta en otra aplicacion: un NavLink no lo puede resolver
        // porque su ruta no existe en este router. Necesita un enlace de
        // verdad que provoque una carga completa.
        external ? (
          <a
            key={key}
            href={to(language)}
            className="relative flex items-center gap-3 rounded-[var(--btn-radius)] px-3 py-2.5 text-sm font-medium text-ink-muted transition-colors hover:bg-surface-2/70 hover:text-ink"
          >
            <Icon className="h-5 w-5" />
            <span>{t(label)}</span>
          </a>
        ) : (
        <NavLink
          key={key}
          to={to(language)}
          end={key === 'home'}
          className={({ isActive }) =>
            cn(
              'relative flex items-center gap-3 rounded-[var(--btn-radius)] px-3 py-2.5 text-sm font-medium transition-colors',
              isActive
                ? // Placa invertida en el parque; barra carmesi en el canto en
                  // la portada. Lo decide el mundo (index.css).
                  'nav-side-active'
                : 'text-ink-muted hover:bg-surface-2/70 hover:text-ink',
            )
          }
        >
          <Icon className="h-5 w-5 shrink-0" />
          {t(label)}
        </NavLink>
      ))}
    </nav>
  );
}

function BottomNav({ items }: { items: NavItem[] }) {
  const { t } = useTranslation();
  const language = useLanguage();

  return (
    <nav
      className="shell-bar-bottom fixed inset-x-0 bottom-0 z-40 pb-[env(safe-area-inset-bottom)] md:hidden"
      aria-label={t('nav.menu')}
    >
      <div className="tube-rule" aria-hidden="true" />
      <ul className="flex">
        {items.slice(0, 5).map(({ key, to, label, Icon, external }) => (
          <li key={key} className="flex-1">
            {external ? (
              <a
                href={to(language)}
                className="flex h-full flex-col items-center gap-1 py-2.5 text-ink-muted transition-colors"
              >
                <Icon className="h-5 w-5" />
                <span className="nav-bottom-label max-w-full truncate px-1">{t(label)}</span>
              </a>
            ) : (
              <NavLink
                to={to(language)}
                end={key === 'home'}
                className={({ isActive }) =>
                  cn(
                    'flex h-full flex-col items-center gap-1 py-2.5 transition-colors',
                    isActive ? 'nav-bottom-active' : 'text-ink-muted',
                  )
                }
              >
                <Icon className="h-5 w-5" />
                <span className="nav-bottom-label max-w-full truncate px-1">{t(label)}</span>
              </NavLink>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function LanguageSwitcher({ compact = true }: { compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const current = useLanguage();
  const [open, setOpen] = useState(false);

  const change = async (next: Language) => {
    setOpen(false);
    if (next === current) return;
    // Cambio en caliente, sin recargar y sin perder la pantalla (RF-LANG-05).
    await i18n.changeLanguage(next);
    document.documentElement.lang = next;
    navigate(translatePath(location.pathname, next) + location.search, { replace: true });
  };

  if (!compact) {
    return (
      <div className="flex gap-2">
        {SUPPORTED_LANGUAGES.map((code) => (
          <Button
            key={code}
            variant={code === current ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => void change(code)}
          >
            {t(code === 'es' ? 'language.spanish' : 'language.english')}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 rounded-[var(--btn-radius)] px-2 py-2 text-sm text-ink-muted hover:bg-surface-2 hover:text-ink"
        // El nombre accesible empieza por lo que se ve ("ES"): quien usa voz
        // dice lo que lee en pantalla (WCAG 2.5.3).
        aria-label={`${current.toUpperCase()} · ${t('language.change')}`}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <IconGlobe className="h-5 w-5" />
        <span className="text-xs font-semibold uppercase">{current}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            role="menu"
            className="absolute right-0 z-20 mt-1 w-36 overflow-hidden rounded-[var(--btn-radius)] border border-line bg-surface shadow-card"
          >
            {SUPPORTED_LANGUAGES.map((code) => (
              <button
                key={code}
                role="menuitemradio"
                aria-checked={code === current}
                type="button"
                onClick={() => void change(code)}
                className={cn(
                  'block w-full px-3 py-2 text-left text-sm hover:bg-surface-2',
                  code === current && 'font-semibold text-accent',
                )}
              >
                {t(code === 'es' ? 'language.spanish' : 'language.english')}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function ThemeToggle() {
  const { t } = useTranslation();
  const { theme, toggle } = useTheme();

  return (
    <button
      type="button"
      onClick={toggle}
      className="rounded-[var(--btn-radius)] p-2 text-ink-muted hover:bg-surface-2 hover:text-ink"
      aria-label={t(theme === 'dark' ? 'settings.themeLight' : 'settings.themeDark')}
    >
      {theme === 'dark' ? <IconSun className="h-5 w-5" /> : <IconMoon className="h-5 w-5" />}
    </button>
  );
}

function SiteFooter() {
  const { t } = useTranslation();
  const language = useLanguage();

  return (
    // Visible tambien en movil, que es donde mas se usa: es la unica entrada a
    // "Quien soy" para quien no ha iniciado sesion. El `pb-24` deja sitio a la
    // barra de navegacion inferior.
    <footer className="mt-auto border-t border-line/60 pb-24 pt-6 text-sm text-ink-muted md:py-8">
      <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-4 px-4 md:px-6">
        <span>© {new Date().getFullYear()} CaliPeak</span>
        <nav className="flex flex-wrap gap-x-4 gap-y-2" aria-label={t('settings.legal')}>
          <NavLink to={routes.about(language)} className="py-1 hover:text-ink">
            {t('about.navLabel')}
          </NavLink>
          <NavLink to={routes.terms(language)} className="hover:text-ink">
            {t('legal.terms')}
          </NavLink>
          <NavLink to={routes.privacy(language)} className="hover:text-ink">
            {t('legal.privacy')}
          </NavLink>
        </nav>
      </div>
    </footer>
  );
}
